
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import image_google from "../../../assets/img/images.png";
import "../../../assets/css/Youtube.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const YOUTUBE_URL = process.env.REACT_APP_YOUTUBE_URL;
const GOOGLE_URL = process.env.REACT_APP_GOOGLE_URL;

function Youtube() {
  const { section, id } = useParams();

  const [servicesData, setServicesData] = useState([]);
  const [pageYoutube, setPageYoutube] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [youtubePagination, setYoutubePagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 3,
    total: 0,
  });

  const fetchYoutubeData = useCallback(async (page) => {
    try {
      setLoading(true);
      setLoadError(false);

      await axios.get("sanctum/csrf-cookie");

      const response = await axios.get(
        `${BASE_URL}posdata?columes[eq]=title_advert_utube&ministry[eq]=1&status02[eq]=publish&status[eq]=Public&visibility[eq]=Public&page=${page}&per_page=3`
      );

      if (response?.data?.status === 200) {
        setServicesData(response.data.allData || []);

        setYoutubePagination(
          response.data.pagination || {
            current_page: page,
            last_page: 1,
            per_page: 3,
            total: 0,
          }
        );
      } else {
        setServicesData([]);
      }
    } catch (error) {
      console.error("Unable to load media spotlight:", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchYoutubeData(pageYoutube);
  }, [fetchYoutubeData, pageYoutube, section, id]);

  const changePage = (page) => {
    if (
      page >= 1 &&
      page <= youtubePagination.last_page &&
      page !== pageYoutube
    ) {
      setPageYoutube(page);
    }
  };

  const getVideoDetails = (service) => {
    const rawAddress = service?.addressLink || "";
    const parts = rawAddress.split("*Locat*");
    const videoIdOrPath = (parts[0] || "").trim();
    const provider = (parts[1] || "").trim().toLowerCase();

    const isYouTube = provider === "youtube";

    return {
      title: service?.title || "Media Spotlight",
      isYouTube,
      videoUrl: isYouTube
        ? `${YOUTUBE_URL || ""}${videoIdOrPath}`
        : `${GOOGLE_URL || ""}${videoIdOrPath}`,
      embedUrl: isYouTube
        ? `${YOUTUBE_URL || ""}${videoIdOrPath}`
        : "",
    };
  };

  if (!loading && servicesData.length === 0 && !loadError) {
    return null;
  }

  return (
    <section
      id="services"
      className="youtube-section section"
      aria-labelledby="youtube-heading"
    >
      <div className="container">
        {/* SECTION HEADING */}
        <header className="yt-heading" data-aos="fade-up">
          <div className="yt-heading-copy">
            <span className="yt-eyebrow">
              <i className="fa-solid fa-circle-play" aria-hidden="true" />
              Official Ministry Media
            </span>

            <h2 id="youtube-heading">
              Media <span>Spotlight</span>
            </h2>

            {/* <p>
              Bringing policies to life through videos, public information,
              ministry activities, and stories of national development.
            </p> */}
          </div>

          <div className="yt-heading-icon" aria-hidden="true">
            <i className="fa-solid fa-photo-film" />
          </div>
        </header>

        {/* LOADING STATE */}
        {loading && (
          <div className="yt-state" role="status">
            <span className="yt-spinner" />
            <p>Loading ministry videos...</p>
          </div>
        )}

        {/* ERROR STATE */}
        {!loading && loadError && (
          <div className="yt-state yt-state-error" role="alert">
            <i className="fa-solid fa-triangle-exclamation" />
            <p>
              We could not load the media spotlight. Please try again.
            </p>

            <button
              type="button"
              className="yt-retry-button"
              onClick={() => fetchYoutubeData(pageYoutube)}
            >
              Try Again
            </button>
          </div>
        )}

        {/* VIDEO CARDS */}
        {!loading && !loadError && servicesData.length > 0 && (
          <>
            <div className="yt-grid">
              {servicesData.map((service, index) => {
                const video = getVideoDetails(service);

                return (
                  <article
                    className="yt-card"
                    key={service.id || index}
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                  >
                    <div className="yt-video-frame">
                      {video.isYouTube && video.embedUrl ? (
                        <iframe
                          src={video.embedUrl}
                          title={video.title}
                          loading="lazy"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allowFullScreen
                        />
                      ) : (
                        <a
                          className="yt-external-preview"
                          href={video.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open ${video.title}`}
                        >
                          <img
                            src={image_google}
                            alt=""
                            loading="lazy"
                          />

                          <span className="yt-external-play">
                            <i className="fa-solid fa-play" />
                          </span>

                          <span className="yt-external-label">
                            View Media
                            <i className="fa-solid fa-arrow-up-right-from-square" />
                          </span>
                        </a>
                      )}
                    </div>

                    <div className="yt-card-body">
                      <div className="yt-card-meta">
                        <span className="yt-card-dot" />
                        {video.isYouTube ? "YouTube Video" : "External Media"}
                      </div>

                      <h3>{video.title}</h3>

                      <a
                        className="yt-card-link"
                        href={video.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {video.isYouTube ? "Watch on YouTube" : "View Media"}
                        <i
                          className="fa-solid fa-arrow-up-right-from-square"
                          aria-hidden="true"
                        />
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>

            {/* PAGINATION */}
            {youtubePagination.last_page > 1 && (
              <nav
                className="yt-pagination"
                aria-label="Media spotlight pagination"
              >
                <div className="yt-pagination-info">
                  Page <strong>{youtubePagination.current_page}</strong> of{" "}
                  <strong>{youtubePagination.last_page}</strong>

                  {youtubePagination.total > 0 && (
                    <span> · {youtubePagination.total} videos</span>
                  )}
                </div>

                <div className="yt-pagination-controls">
                  <button
                    type="button"
                    onClick={() => changePage(1)}
                    disabled={pageYoutube === 1 || loading}
                    aria-label="First page"
                  >
                    <i className="fa-solid fa-angles-left" />
                    <span>First</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => changePage(pageYoutube - 1)}
                    disabled={pageYoutube === 1 || loading}
                    aria-label="Previous page"
                  >
                    <i className="fa-solid fa-angle-left" />
                    <span>Previous</span>
                  </button>

                  <span className="yt-current-page">
                    {youtubePagination.current_page}
                  </span>

                  <button
                    type="button"
                    onClick={() => changePage(pageYoutube + 1)}
                    disabled={
                      pageYoutube === youtubePagination.last_page || loading
                    }
                    aria-label="Next page"
                  >
                    <span>Next</span>
                    <i className="fa-solid fa-angle-right" />
                  </button>

                  <button
                    type="button"
                    onClick={() => changePage(youtubePagination.last_page)}
                    disabled={
                      pageYoutube === youtubePagination.last_page || loading
                    }
                    aria-label="Last page"
                  >
                    <span>Last</span>
                    <i className="fa-solid fa-angles-right" />
                  </button>
                </div>
              </nav>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default React.memo(Youtube);
